import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SCHOOL } from '../apollo/queries/school';
import { GETPLAYER } from '../apollo/queries/getPlayer';
import { useLazyQuery, useQuery } from '@apollo/client';
import TeamBanner from '../components/teamBanner/teamBanner';
import Loader from '../components/loader/loader';
import Error from '../components/error/error';
import { getTeamConfig } from '../config/config';
import styles from './pages.module.scss';
import Tabs from '../components/tabs/tabs';
import ResultsTable from '../components/resultsTable/resultsTable';
import {
  getOpponentsRosterIds,
  overviewResults,
  parseData,
  prepareSeasonsData
} from '../components/utilities/utilities';
import EmptySchedule from '../components/emptySchedule/emptySchedule';
import { PLAYERMATCHES } from '../apollo/queries/getPlayerMatches';
import { createTennisDataClient } from '../apollo/client';
import { activeFiltersActions, useActiveFilters } from '../context/active-filter-context';
import { IListSeasons } from '../graphql-types/listSeasons';
import useListSeasons from '../customHooks/useListSeasons';
import Overview from '../components/overview/overview';
import Filter from '../components/filter/filter';
import { GET_TEAMS } from '../apollo/queries/getTeams';
import { WTNPayload } from '../graphql-types/wtn';
import { MATCH_UPS_FILTER_STATUSES } from '../constants/filters';

const tennisDataClient = createTennisDataClient();

const IS_PLAYER_PAGE = true;

const Player: React.FC = () => {
  const thisTeam = getTeamConfig();
  const { id } = useParams();
  const { state, dispatch } = useActiveFilters();
  const { seasonsData, seasonsLoading, seasonsError, seasonYear } = useListSeasons();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [tableWidth, setTableWith] = useState(0);

  const [matches, setMatches] = useState('singles');

  const setContainerRef = useCallback(node => {
    if (node) {
      // Check if a node is actually passed. Otherwise node would be null.
      // You can now do what you need to, addEventListeners, measure, etc.
      const { paddingRight, paddingLeft } = window.getComputedStyle(node);
      const contentWidth = node.clientWidth - parseFloat(paddingRight) - parseFloat(paddingLeft);
      setTableWith(contentWidth);
    }

    // Save a reference to the node
    containerRef.current = node;
  }, []);

  let SCHOOL_VARS = {
    variables: {
      id: thisTeam.schoolId
    }
  };

  let PLAYERMATCHES_VARS = {
    client: tennisDataClient,
    variables: {
      personFilter: {
        ids: [
          {
            type: 'PersonID',
            identifier: id
          }
        ]
      },
      filter: {
        start: {
          after: `${seasonYear}-08-01`
        },
        end: {
          before: `${Number(seasonYear) + 1}-07-31`
        },
        statuses: MATCH_UPS_FILTER_STATUSES
      }
    }
  };

  const listOfSeasons: IListSeasons[] = seasonsData?.listSeasons;

  const {
    loading: schoolLoading,
    error: schoolError,
    data: schoolData
  } = useQuery(SCHOOL, SCHOOL_VARS);

  const {
    loading: plLoading,
    error: plError,
    data: plData
  } = useQuery(GETPLAYER, {
    variables: {
      rosterId: thisTeam.teamId && thisTeam.teamId !== null ? thisTeam.teamId.toLowerCase() : '',
      uniqueId: id,
      uniqueIdType: 'personId'
    }
  });

  const thisPlayer = useMemo(() => plData?.getRosterMember[0], [plData?.getRosterMember]);

  const [playerMatches, { loading: matchesLoading, error: matchesError, data: matchesData }] =
    useLazyQuery(PLAYERMATCHES, PLAYERMATCHES_VARS);

  const rosterIds = useMemo(() => getOpponentsRosterIds(matchesData, id), [id, matchesData]);

  const [getTeamsData, { data: teamsData, loading: teamsLoading, error: teamsError }] =
    useLazyQuery(GET_TEAMS);

  useEffect(() => {
    if (rosterIds && rosterIds.length > 0) {
      getTeamsData({ variables: { rosterIds } });
    }
  }, [getTeamsData, rosterIds]);

  const rowData = useMemo(
    () => parseData(matchesData, teamsData?.rostersByIds, id),
    [id, matchesData, teamsData?.rostersByIds]
  );

  const partners = useMemo(
    () =>
      rowData.doubles.map((doubles: any) => {
        const player = doubles.thisPlayers?.players.find(
          (player: any) => player?.person?.personID !== id
        )?.person;

        return player?.personID
          ? {
              label: `${player?.nativeGivenName} ${player?.nativeFamilyName}`,
              value: player?.personID
            }
          : {
              label: 'Player',
              value: undefined
            };
      }),
    [id, rowData.doubles]
  );

  const partnerUniqueOptions = useMemo(
    () =>
      partners
        .filter((value: any, index: number) => {
          const _value = JSON.stringify(value);
          return (
            index ===
            partners.findIndex((partner: any) => {
              return JSON.stringify(partner) === _value;
            })
          );
        })
        .sort((a: any, b: any) => {
          const nameA = a.label.toLowerCase();
          const nameB = b.label.toLowerCase();
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        }),
    [partners]
  );

  useEffect(() => {
    dispatch(activeFiltersActions.setPartnerOptions({ options: partnerUniqueOptions }));
  }, [dispatch, partnerUniqueOptions]);

  const filteredDoubles = useMemo(
    () =>
      state.filters.PARTNER.value
        ? rowData.doubles.filter(
            (doubles: any) =>
              doubles.thisPlayers.players.find((player: any) => player.person.personID !== id)
                .person.personID === state.filters.PARTNER.value
          )
        : rowData.doubles,
    [id, rowData.doubles, state.filters.PARTNER.value]
  );

  useEffect(() => {
    if (state.season?.value) {
      playerMatches();
    }
  }, [playerMatches, state.season?.value]);

  const wtnDataByMatchType = () => {
    const singleWtn = thisPlayer.worldTennisNumbers?.length
      ? thisPlayer.worldTennisNumbers?.find((wtn: WTNPayload) => wtn.type === 'SINGLE')
      : {};
    const doubleWtn = thisPlayer.worldTennisNumbers?.length
      ? thisPlayer.worldTennisNumbers?.find((wtn: WTNPayload) => wtn.type === 'DOUBLE')
      : {};

    return {
      tennisNumberSingles: singleWtn?.tennisNumber,
      confidenceSingles: singleWtn?.confidence,
      isRankedSingles: singleWtn?.isRanked,
      tennisNumberDoubles: doubleWtn?.tennisNumber,
      confidenceDoubles: doubleWtn?.confidence,
      isRankedDoubles: doubleWtn?.isRanked
    };
  };

  if (schoolLoading || plLoading || matchesLoading || seasonsLoading || teamsLoading) {
    return <Loader />;
  }
  if (schoolError || (plError && !plData) || matchesError || seasonsError || teamsError) {
    return <Error />;
  }

  return (
    <>
      <TeamBanner
        teamData={schoolData.school}
        thisTeam={thisTeam}
        thisPlayer={thisPlayer}
        view="player"
        options={prepareSeasonsData(listOfSeasons)}
        wtnData={wtnDataByMatchType()}
      />

      <Tabs>
        <ul>
          <li>
            <button
              data-testid="PT-player-singlesLinkButton"
              className={matches === 'singles' ? styles.selected : ''}
              onClick={() => {
                setMatches('singles');
              }}
            >
              Singles
            </button>
          </li>
          <li>
            <button
              data-testid="PT-player-doublesLinkButton"
              className={matches === 'doubles' ? styles.selected : ''}
              onClick={() => {
                setMatches('doubles');
              }}
            >
              Doubles
            </button>
          </li>
        </ul>
      </Tabs>

      <div ref={setContainerRef} className={styles.siteWidth}>
        {matches === 'doubles' && <Filter />}
        {rowData[matches].length > 0 && (
          <Overview
            heading="Overview"
            {...overviewResults(
              matches === 'singles' ? rowData.singles : filteredDoubles,
              IS_PLAYER_PAGE
            )}
            matchType={matches}
          />
        )}
        <div className={styles.playerProfile}>
          <div id="singles" className={matches === 'singles' ? '' : styles.hidden}>
            <h2>Singles results</h2>
            <p>It may take up to 24 hours for reported results to appear on this page.</p>
            {rowData.singles.length ? (
              <ResultsTable
                tableWidth={tableWidth}
                data={rowData.singles}
                id={id}
                type="singles"
                thisPlayer={thisPlayer}
              />
            ) : (
              <div className={styles.boxEmpty}>
                <EmptySchedule
                  type=""
                  title="No Results"
                  copy="There are no recorded results yet"
                />
              </div>
            )}
          </div>

          <div id="doubles" className={matches === 'doubles' ? '' : styles.hidden}>
            <h2>Doubles results</h2>
            <p>It may take up to 24 hours for reported results to appear on this page.</p>
            {rowData.doubles.length ? (
              <ResultsTable
                tableWidth={tableWidth}
                data={filteredDoubles}
                id={id}
                type="doubles"
                thisPlayer={thisPlayer}
              />
            ) : (
              <div className={styles.boxEmpty}>
                <EmptySchedule
                  type=""
                  title="No Results"
                  copy="There are no recorded results yet"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Player;
